import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { notifyOk } from "@/lib/notify";
import { Eye, EyeOff } from "lucide-react";

function PasswordField({ label, value, onChange, testId, error, hint, autoComplete }) {
  const [visible, setVisible] = useState(false);
  return (
    <div>
      <Label className="text-slate-200">{label}</Label>
      <div className="relative mt-1">
        <Input
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          autoComplete={autoComplete}
          required
          className={`h-11 rounded-xl border bg-white/10 pr-12 text-white caret-white placeholder:text-slate-400 focus-visible:ring-[#01CBE3] ${
            error ? "border-red-400/70" : "border-white/25"}`}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          data-testid={`${testId}-toggle`}
          aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
          className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-slate-300 hover:text-white"
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {error ? (
        <p data-testid={`${testId}-error`} className="mt-1 text-xs text-red-300">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-slate-400">{hint}</p>
      ) : null}
    </div>
  );
}

export default function CambiarPassword() {
  const { user, aplicarToken, logout } = useAuth();
  const navigate = useNavigate();
  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [repite, setRepite] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [errActual, setErrActual] = useState("");
  const [errNueva, setErrNueva] = useState("");
  const [errGlobal, setErrGlobal] = useState("");
  const [tocado, setTocado] = useState(false);

  const obligatorio = !!user?.must_change_password;

  const nuevaInvalida =
    nueva.length > 0 && nueva.length < 10
      ? "Mínimo 10 caracteres."
      : nueva.length >= 10 && (/^\d+$/.test(nueva) || /^[a-zA-Z]+$/.test(nueva))
      ? "Combina letras con números o símbolos."
      : "";
  const noCoinciden = repite.length > 0 && nueva !== repite ? "Las contraseñas no coinciden." : "";
  const valido = actual.length > 0 && nueva.length >= 10 && !nuevaInvalida && nueva === repite;

  const enviar = async (e) => {
    e.preventDefault();
    setTocado(true);
    setErrActual("");
    setErrNueva("");
    setErrGlobal("");
    if (!valido) return;
    setEnviando(true);
    try {
      const { data } = await api.post("/auth/cambiar-password", {
        password_actual: actual,
        password_nuevo: nueva,
      });
      aplicarToken(data.access_token, { ...user, must_change_password: false });
      notifyOk("Contraseña actualizada.");
      navigate("/consola-v3", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const msg = formatApiErrorDetail(err?.response?.data?.detail);
      if (status === 401) setErrActual(msg);
      else if (/contrase/i.test(msg) && /nueva|caracteres|combinar|distinta/i.test(msg)) setErrNueva(msg);
      else setErrGlobal(msg);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#061024] flex items-center justify-center px-4 py-10">
      <form
        onSubmit={enviar}
        data-testid="cambiar-password-form"
        className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-8 backdrop-blur"
      >
        <h1 className="text-2xl font-semibold text-white">
          {obligatorio ? "Define tu contraseña" : "Cambiar contraseña"}
        </h1>
        <p className="mt-2 text-sm text-slate-300">
          {obligatorio
            ? "Tu acceso se creó con una credencial temporal. Define una contraseña propia para continuar."
            : "Al guardar se cerrarán las demás sesiones de tu cuenta."}
        </p>

        {errGlobal && (
          <p data-testid="password-error-global"
            className="mt-4 rounded-xl border border-red-400/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">
            {errGlobal}
          </p>
        )}

        <div className="mt-6 space-y-4">
          <PasswordField
            label={obligatorio ? "Credencial temporal" : "Contraseña actual"}
            value={actual}
            onChange={(v) => { setActual(v); setErrActual(""); }}
            testId="password-actual-input"
            autoComplete="current-password"
            error={errActual || (tocado && !actual ? "Escribe tu contraseña actual." : "")}
          />
          <PasswordField
            label="Contraseña nueva"
            value={nueva}
            onChange={(v) => { setNueva(v); setErrNueva(""); }}
            testId="password-nuevo-input"
            autoComplete="new-password"
            error={errNueva || nuevaInvalida}
            hint="Mínimo 10 caracteres, combinando letras con números o símbolos."
          />
          <PasswordField
            label="Repite la contraseña nueva"
            value={repite}
            onChange={setRepite}
            testId="password-repite-input"
            autoComplete="new-password"
            error={noCoinciden}
          />
        </div>
        <Button
          type="submit"
          disabled={enviando || (tocado && !valido)}
          data-testid="cambiar-password-submit"
          className="mt-6 w-full rounded-full bg-[#01CBE3] text-[#012472] hover:bg-[#01CBE3]/90"
        >
          {enviando ? "Guardando…" : "Guardar contraseña"}
        </Button>
        <button
          type="button"
          onClick={() => {
            logout();
            navigate("/login", { replace: true });
          }}
          data-testid="cambiar-password-salir"
          className="mt-4 w-full text-xs text-slate-400 hover:text-slate-200"
        >
          Salir
        </button>
      </form>
    </div>
  );
}
